import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: number | string | null | undefined;
  icon?: ReactNode;
  color?: 'blue' | 'green' | 'red' | 'purple' | 'orange';
  suffix?: string;
  trend?: number;
  trendLabel?: string;
  loading?: boolean;
  className?: string;
  tooltip?: string;
}

const colorClasses = {
  blue: 'border-l-4 border-l-blue-500',
  green: 'border-l-4 border-l-green-500',
  red: 'border-l-4 border-l-red-500',
  purple: 'border-l-4 border-l-purple-500',
  orange: 'border-l-4 border-l-orange-500',
};

const iconColorClasses = {
  blue: 'text-blue-500',
  green: 'text-green-500',
  red: 'text-red-500',
  purple: 'text-purple-500',
  orange: 'text-orange-500',
};

function formatValue(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

export function MetricCard({
  title,
  value,
  icon,
  color = 'blue',
  suffix,
  trend,
  trendLabel,
  loading = false,
  className,
  tooltip,
}: MetricCardProps) {
  if (loading) {
    return (
      <Card className={cn(colorClasses[color], className)}>
        <CardContent className="p-4">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-16" />
        </CardContent>
      </Card>
    );
  }

  const formattedValue = formatValue(value);
  const hasTrend = trend !== undefined && trend !== null;

  return (
    <Card className={cn(colorClasses[color], className)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-1">
              <p className="text-sm text-muted-foreground">{title}</p>
              {tooltip && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
                        aria-label={`Info about ${title}`}
                      >
                        <Info className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground cursor-help" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold">{formattedValue}</span>
              {suffix && (
                <span className="text-sm text-muted-foreground">{suffix}</span>
              )}
            </div>
            {hasTrend && (
              <div className="flex items-center gap-1 mt-1">
                <span
                  className={cn(
                    'text-xs font-medium',
                    trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-muted-foreground'
                  )}
                >
                  {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'}
                  {Math.abs(trend)}%
                </span>
                {trendLabel && (
                  <span className="text-xs text-muted-foreground">{trendLabel}</span>
                )}
              </div>
            )}
          </div>
          {icon && (
            <div className={cn('text-2xl', iconColorClasses[color])}>
              {icon}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
